import React, { useState } from "react";
import axios from "axios";
import CryptoJS from "crypto-js";
import JSZip from "jszip";
import "./App.css";
import banner from "./assets/banner.webp";

const API_URL = process.env.REACT_APP_BACK_API_URL || "http://localhost:5555";

function App() {
  const [files, setFiles] = useState([]);
  const [uploadResults, setUploadResults] = useState([]);
  const [token, setToken] = useState("");
  const [enableEncryption, setEnableEncryption] = useState(false);
  const [enableCompression, setEnableCompression] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [ttlValue, setTtlValue] = useState("");
  const [ttlUnit, setTtlUnit] = useState("m");
  const [options, setOptions] = useState({
    oneshot: false,
    removable: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  const [copiedLinks, setCopiedLinks] = useState({});
  const [copiedCmds, setCopiedCmds] = useState({});
  const [allLinksCopied, setAllLinksCopied] = useState(false);
  const [allCmdsCopied, setAllCmdsCopied] = useState(false);

  const handleFileChange = (event) => {
    setFiles([...event.target.files]);
  };

  const handleTokenChange = (event) => {
    setToken(event.target.value);
  };

  const handleEncryptionChange = (event) => {
    setEnableEncryption(event.target.checked);
  };

  const handleCompressionChange = (event) => {
    setEnableCompression(event.target.checked);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleOptionChange = (event) => {
    const { name, checked } = event.target;
    setOptions((prev) => ({ ...prev, [name]: checked }));
  };

  const handleTtlValueChange = (event) => {
    setTtlValue(event.target.value);
  };

  const handleTtlUnitChange = (event) => {
    setTtlUnit(event.target.value);
  };

  const copyToClipboard = (text, setStateFn, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setStateFn((prev) => ({ ...prev, [id]: true }));
      setTimeout(() => {
        setStateFn((prev) => ({ ...prev, [id]: false }));
      }, 2000);
    });
  };

  function generateRandomPassword(length = 16) {
    const charset =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let ret = "";
    const cryptoObj = window.crypto || window.msCrypto;
    for (let i = 0; i < length; i++) {
      const randomValue = cryptoObj.getRandomValues(new Uint32Array(1))[0];
      ret += charset[randomValue % charset.length];
    }
    return ret;
  }

  function hexStringToUint8Array(hexString) {
    let result = [];
    for (let i = 0; i < hexString.length; i += 2) {
      result.push(parseInt(hexString.substr(i, 2), 16));
    }
    return new Uint8Array(result);
  }

  // Fonction pour chiffrer un blob (utilisée pour l'archive ZIP)
  const encryptBlob = (blob, finalPassword) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const arrayBuffer = e.target.result;
          const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
          const salt = CryptoJS.lib.WordArray.random(8);
          const keySize = 256 / 32;
          const ivSize = 128 / 32;
          const key = CryptoJS.algo.EvpKDF.create({
            keySize: keySize + ivSize,
            iterations: 1,
          }).compute(finalPassword, salt);
          const keyWords = CryptoJS.lib.WordArray.create(
            key.words.slice(0, keySize)
          );
          const iv = CryptoJS.lib.WordArray.create(
            key.words.slice(keySize, keySize + ivSize)
          );
          const encrypted = CryptoJS.AES.encrypt(wordArray, keyWords, {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
          });
          const saltedPrefix = CryptoJS.enc.Utf8.parse("Salted__");
          const openSSLCiphertext = saltedPrefix
            .concat(salt)
            .concat(encrypted.ciphertext);
          const encryptedHex = openSSLCiphertext.toString(
            CryptoJS.enc.Hex
          );
          const encryptedBytes = hexStringToUint8Array(encryptedHex);
          const encryptedBlob = new Blob([encryptedBytes], {
            type: "application/octet-stream",
          });
          resolve(encryptedBlob);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (err) => {
        reject(err);
      };
      reader.readAsArrayBuffer(blob);
    });
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError("Veuillez sélectionner au moins un fichier.");
      return;
    }
    if (!token) {
      setError("Veuillez entrer un token Plik.");
      return;
    }
    setError("");
    setUploadResults([]);
    setLoading(true);
    let finalPassword = password;
    if (enableEncryption && !finalPassword) {
      finalPassword = generateRandomPassword(16);
      setPassword(finalPassword);
    }

    // Préparation des options globales d'upload
    const formData = new FormData();
    formData.append("OneShot", options.oneshot ? "true" : "false");
    formData.append("Removable", options.removable ? "true" : "false");

    if (ttlValue) {
      const numericValue = parseInt(ttlValue, 10);
      let finalTtl = numericValue;
      switch (ttlUnit) {
        case "m":
          finalTtl = numericValue * 60;
          break;
        case "h":
          finalTtl = numericValue * 3600;
          break;
        case "d":
          finalTtl = numericValue * 86400;
          break;
        case "M":
          finalTtl = numericValue * 2592000;
          break;
        default:
          finalTtl = numericValue;
      }
      formData.append("Ttl", finalTtl);
    }

    // Si la compression est activée, on crée une archive ZIP regroupant tous les fichiers
    if (enableCompression) {
      const zip = new JSZip();
      files.forEach((file) => {
        zip.file(file.name, file);
      });
      try {
        // Générer le blob de l'archive ZIP
        let zipBlob = await zip.generateAsync({ type: "blob" });
        // Générer un nom aléatoire pour l'archive ZIP
        let archiveName = generateRandomPassword(10) + ".zip";
        // Si le chiffrement est activé, on chiffre l'archive entière
        if (enableEncryption) {
          zipBlob = await encryptBlob(zipBlob, finalPassword);
          archiveName += ".enc";
        }
        formData.append("file", zipBlob, archiveName);

        const uploadUrl = `${API_URL}/upload`;
        const response = await axios.post(uploadUrl, formData, {
          headers: { "X-PlikToken": token },
        });

        setUploadResults([
          {
            fileName: archiveName,
            fileUrl: response.data.file_url,
            decryptCmd: enableEncryption
              ? `curl -s ${response.data.file_url} | openssl aes-256-cbc -d -md md5 -pass pass:${finalPassword} > ${archiveName.replace(
                  ".enc",
                  ""
                )}`
              : null,
          },
        ]);
      } catch (err) {
        setError(
          "Échec de l’upload de l’archive : " +
            (err.response?.data?.error || err.message)
        );
      }
    } else {
      // Sinon, on procède comme avant : upload de chaque fichier (chiffré ou non)
      for (const file of files) {
        const formDataFile = new FormData();
        formDataFile.append("OneShot", options.oneshot ? "true" : "false");
        formDataFile.append("Removable", options.removable ? "true" : "false");

        if (ttlValue) {
          const numericValue = parseInt(ttlValue, 10);
          let finalTtl = numericValue;
          switch (ttlUnit) {
            case "m":
              finalTtl = numericValue * 60;
              break;
            case "h":
              finalTtl = numericValue * 3600;
              break;
            case "d":
              finalTtl = numericValue * 86400;
              break;
            case "M":
              finalTtl = numericValue * 2592000;
              break;
            default:
              finalTtl = numericValue;
          }
          formDataFile.append("Ttl", finalTtl);
        }

        try {
          if (enableEncryption) {
            await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = async (e) => {
                try {
                  const arrayBuffer = e.target.result;
                  const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
                  const salt = CryptoJS.lib.WordArray.random(8);
                  const keySize = 256 / 32;
                  const ivSize = 128 / 32;
                  const key = CryptoJS.algo.EvpKDF.create({
                    keySize: keySize + ivSize,
                    iterations: 1,
                  }).compute(finalPassword, salt);
                  const keyWords = CryptoJS.lib.WordArray.create(
                    key.words.slice(0, keySize)
                  );
                  const iv = CryptoJS.lib.WordArray.create(
                    key.words.slice(keySize, keySize + ivSize)
                  );
                  const encrypted = CryptoJS.AES.encrypt(wordArray, keyWords, {
                    iv: iv,
                    mode: CryptoJS.mode.CBC,
                    padding: CryptoJS.pad.Pkcs7,
                  });
                  const saltedPrefix = CryptoJS.enc.Utf8.parse("Salted__");
                  const openSSLCiphertext = saltedPrefix
                    .concat(salt)
                    .concat(encrypted.ciphertext);
                  const encryptedHex = openSSLCiphertext.toString(
                    CryptoJS.enc.Hex
                  );
                  const encryptedBytes = hexStringToUint8Array(encryptedHex);
                  const encryptedBlob = new Blob([encryptedBytes], {
                    type: "application/octet-stream",
                  });
                  formDataFile.append("file", encryptedBlob, file.name + ".enc");
                } catch (error) {
                  setError("Échec du chiffrement : " + error.message);
                } finally {
                  resolve();
                }
              };
              reader.readAsArrayBuffer(file);
            });
          } else {
            formDataFile.append("file", file, file.name);
          }

          const uploadUrl = `${API_URL}/upload`;
          const response = await axios.post(uploadUrl, formDataFile, {
            headers: { "X-PlikToken": token },
          });

          setUploadResults((prev) => [
            ...prev,
            {
              fileName: file.name,
              fileUrl: response.data.file_url,
              decryptCmd: enableEncryption
                ? `curl -s ${response.data.file_url} | openssl aes-256-cbc -d -md md5 -pass pass:${finalPassword} > ${file.name}`
                : null,
            },
          ]);
        } catch (err) {
          setError(
            "Échec de l’upload du fichier " +
              file.name +
              " : " +
              (err.response?.data?.error || err.message)
          );
        }
      }
    }
    setLoading(false);
  };


  const handleResetForm = () => {
    setFiles([]);
    setToken("");
    setEnableEncryption(false);
    setEnableCompression(false);
    setPassword("");
    setShowPassword(false);
    setTtlValue("");
    setTtlUnit("m");
    setOptions({ oneshot: false, removable: false });
    setError("");
    setUploadResults([]);
    setCopiedLinks({});
    setCopiedCmds({});
  };



  const allLinks = uploadResults.map((r) => r.fileUrl).join("\n");
  const allCurlCommands = uploadResults
    .filter((r) => r.decryptCmd)
    .map((r) => r.decryptCmd)
    .join("\n");

  return (
    <div className="container">
      <header className="header">
        <img src={banner} alt="Upload Banner" className="banner-image" />
        <h1>Plik Uploader</h1>
        <p className="subtitle">
          Uploader et chiffrer vos fichiers en toute sécurité
        </p>
      </header>
      <main>
        <div className="form-card">
          {error && <div className="error">{error}</div>}
          <div className="form-group">
            <label className="file-input-label" htmlFor="fileInput">
              Veuillez sélectionner un ou plusieurs fichiers 👆
            </label>
            <input
              id="fileInput"
              type="file"
              className="file-input"
              onChange={handleFileChange}
              multiple
            />
            {files.length > 0 &&
              files.map((file, index) => <p key={index}>{file.name}</p>)}
          </div>
          <div className="form-group">
            <label htmlFor="tokenInput">Plik Token (obligatoire)</label>
            <input
              id="tokenInput"
              type="text"
              value={token}
              onChange={handleTokenChange}
              required
              autoComplete="on"
            />
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              Activer le chiffrement
              <input
                type="checkbox"
                checked={enableEncryption}
                onChange={handleEncryptionChange}
              />
            </label>
          </div>
          {enableEncryption && (
            <div className="form-group">
              <label htmlFor="passwordInput">
                Mot de passe (optionnel, sera généré si vide)
              </label>
              <div className="password-input-container">
                <input
                  id="passwordInput"
                  type={showPassword ? "text" : "password"}
                  placeholder="Mot de passe"
                  value={password}
                  onChange={handlePasswordChange}
                />
                <button
                  type="button"
                  className="password-toggle-button"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? (
                    <span role="img" aria-label="Cacher le mot de passe">
                      👁️
                    </span>
                  ) : (
                    <span role="img" aria-label="Afficher le mot de passe">
                      👁️‍🗨️
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              Compresser les fichiers dans une archive zip
              <input
                type="checkbox"
                checked={enableCompression}
                onChange={handleCompressionChange}
              />
            </label>
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              Supprimer après téléchargement
              <input
                type="checkbox"
                name="oneshot"
                checked={options.oneshot}
                onChange={handleOptionChange}
              />
            </label>
            <label className="checkbox-label">
              Supprimable du serveur à tout moment
              <input
                type="checkbox"
                name="removable"
                checked={options.removable}
                onChange={handleOptionChange}
              />
            </label>
          </div>
          <div className="form-group ttl-group">
            <label>Durée de vie du fichier</label>
            <div className="ttl-inputs">
              <input
                type="number"
                min="0"
                value={ttlValue}
                onChange={handleTtlValueChange}
                placeholder="Valeur"
              />
              <select value={ttlUnit} onChange={handleTtlUnitChange}>
                <option value="m">minutes</option>
                <option value="h">heures</option>
                <option value="d">jours</option>
                <option value="M">mois</option>
              </select>
            </div>
          </div>
          <div className="button-group">
            <button
              className="upload-button"
              onClick={handleUpload}
              disabled={loading}
            >
              Upload 🚀
            </button>
            <button
              className="reset-button"
              onClick={handleResetForm}
              disabled={loading}
            >
              Réinitialiser 🧹
            </button>
          </div>
          {loading && <div className="spinner"></div>}
        </div>
        {uploadResults.length > 0 && (
          <>
            <div className="all-copy-section">
              <button
                className="copy-all-button"
                onClick={() => copyToClipboard(allLinks, setAllLinksCopied)}
              >
                {allLinksCopied ? "✓ Tous les liens copiés" : "Copier tous les liens"}
              </button>
              {enableEncryption && allCurlCommands && (
                <button
                  className="copy-all-button"
                  onClick={() =>
                    copyToClipboard(allCurlCommands, setAllCmdsCopied)
                  }
                >
                  {allCmdsCopied
                    ? "✓ Toutes les commandes copiées"
                    : "Copier toutes les commandes curl"}
                </button>
              )}
            </div>
            {uploadResults.map((result, index) => (
              <div key={index} className="result-card">
                <p>Fichier {result.fileName} uploadé avec succès !</p>
                <div className="copy-section">
                  <div className="link-container">
                    <a
                      href={result.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-text"
                    >
                      {result.fileUrl}
                    </a>
                    <button
                      className={`copy-button ${
                        copiedLinks[result.fileUrl] ? "copied" : ""
                      }`}
                      onClick={() =>
                        copyToClipboard(result.fileUrl, setCopiedLinks, result.fileUrl)
                      }
                      title="Copier le lien"
                    >
                      {copiedLinks[result.fileUrl] ? "✓" : "📋"}
                    </button>
                  </div>
                </div>
                {enableEncryption && result.decryptCmd && (
                  <div className="decrypt-section">
                    <p>Commande pour télécharger et décrypter le fichier :</p>
                    <div className="code-container">
                      <code>{result.decryptCmd}</code>
                      <button
                        className={`copy-button ${
                          copiedCmds[result.fileUrl] ? "copied" : ""
                        }`}
                        onClick={() =>
                          copyToClipboard(result.decryptCmd, setCopiedCmds, result.fileUrl)
                        }
                        title="Copier la commande"
                      >
                        {copiedCmds[result.fileUrl] ? "✓" : "📋"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </main>
      <footer className="footer">
        <p>© 2025 Plik Uploader - Alter Way. Tous droits réservés.</p>
      </footer>
    </div>
  );
}

export default App;
